import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X } from "lucide-react";
import { useModalBack } from "@/hooks/useModalBack";
import { peekBranding } from "@/lib/branding";

const DISMISS_KEY = "elite_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days
export const INSTALL_EVENT = "elite:open-install-prompt";

export function openInstallPrompt() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(INSTALL_EVENT));
}

export function isInstalled() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isStandalone() {
  return isInstalled();
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch (e) {
    return false;
  }
}

export default function InstallPrompt() {
  const appName = peekBranding()?.app_name || "Portal";
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!recentlyDismissed()) {
        // small delay so the prompt doesn't jump in over the splash
        setTimeout(() => setOpen(true), 1200);
      }
    };
    const onInstalled = () => {
      setOpen(false);
      setDeferred(null);
    };
    const onManualOpen = () => {
      // Manual trigger from Settings menu — bypass the 7-day dismissal.
      try {
        localStorage.removeItem(DISMISS_KEY);
      } catch (e) {
        /* ignore */
      }
      if (isIos()) setIosHint(true);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener(INSTALL_EVENT, onManualOpen);

    // iOS Safari never fires beforeinstallprompt. Show manual instructions
    // instead – but only once per week.
    if (isIos() && !recentlyDismissed()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setOpen(true);
      }, 2000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
        window.removeEventListener(INSTALL_EVENT, onManualOpen);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener(INSTALL_EVENT, onManualOpen);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      /* ignore quota / disabled storage */
    }
    setOpen(false);
  };

  useModalBack(open, dismiss);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome !== "accepted") {
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch (e) {
          /* ignore quota / disabled storage */
        }
      }
    } catch (e) {
      /* ignore rejected prompts */
    }
    setDeferred(null);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent
        className="max-w-md border-white/10 bg-[#0b0b0f] text-white"
        data-testid="install-prompt-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img
              src="/api/branding/icon/icon-192.png"
              alt={appName}
              className="h-14 w-14 rounded-2xl ring-1 ring-white/10"
            />
            <div>
              <DialogTitle className="text-xl">Install {appName}</DialogTitle>
              <DialogDescription className="text-white/60">
                Add the {appName} portal to your device for a full-screen, one-tap
                experience.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {iosHint ? (
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-sm text-white/80">
            <p className="mb-2 font-medium text-white">On iPhone / iPad:</p>
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                Tap the <Share className="inline h-4 w-4 -translate-y-0.5" />{" "}
                <span className="font-medium">Share</span> button in Safari.
              </li>
              <li>
                Choose{" "}
                <span className="font-medium">
                  Add to Home Screen{" "}
                  <Plus className="inline h-4 w-4 -translate-y-0.5" />
                </span>
                .
              </li>
              <li>
                Confirm the name{" "}
                <span className="font-semibold text-white">{appName}</span> and tap
                Add.
              </li>
            </ol>
          </div>
        ) : (
          <p className="text-sm text-white/70">
            Install to your phone or laptop home screen and launch {appName} like
            any native app — no browser tabs, no address bar.
          </p>
        )}

        <DialogFooter className="mt-2 flex-row justify-end gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={dismiss}
            data-testid="install-prompt-dismiss"
            className="text-white/70 hover:bg-white/5 hover:text-white"
          >
            <X className="mr-1 h-4 w-4" />
            Not now
          </Button>
          {!iosHint && (
            <Button
              onClick={install}
              disabled={!deferred}
              data-testid="install-prompt-install"
              className="bg-blue-600 text-white hover:bg-blue-500"
            >
              <Download className="mr-1 h-4 w-4" />
              Install
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
